import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types';
import utils from 'helpers/utils';
import assistant from 'helpers/assistant';
import {
  useColumnState,
  useKanbanState,
  useEffectEvent,
  useListSelection,
  useListState,
  useGraphState,
  useAssistantState
} from 'helpers/hooks/utils';
import {useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import {useTour} from 'components/organisms/Providers/TourProvider/TourProvider';

import constants from 'helpers/constants';
import useMediaQuery from '@mui/material/useMediaQuery';

export const TableContext = React.createContext(null);

export function useTable () {
  return React.useContext(TableContext);
}

const TableProvider = (props) => {
  const [internalState, setInternalState] = useState({
    list: null,
    testList: null,
    kanbanLists: {},
    preload: [],
    activeView: null,
    // see also clearState
    showIndex: false,
    showSave: false,
    showAssistant: false,
    showFilters: false,
    showColumns: false,
    showKanban: false,
    showProfile: false,
    showViews: false,
    showQuery: true,
    viewData: null,
    settings: {}
  });

  const viewInitialised = useRef(null);
  const listInitialised = useRef(null);
  const definitionsInitialised = useRef(false);

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  // see also ProfileProvider
  const optionsMemo = useMemo(() => {
    const options = utils.mergeObjects(props?.options ?? {}, props?.view?.options, true);
    Object.keys(options).forEach((k) => {
      if (options[k]?.options && options[k].options.type) {
        if (props?.view || props.skipStorage) {
          if (props?.view && (props?.view?.id !== 'default') && options[k].options.name) {
            if (k === 'listState') {
              options['baseListState'] = {
                options: {
                  ...options[k].options,
                  searchParams: false
                }
              }
            }

            options[k].options.name = `${options[k].options.name}_${props.view.id}`;
          }
          if (props?.view?.skipStorage || props.skipStorage) {
            if (props.skipStorage) {
              options[k].options.searchParams = false;
            }
            options[k].options.type = constants.appState.type.none;
          }
        }
      }
    });

    if (!props?.isDefinitionsLoading || definitionsInitialised.current) {
      definitionsInitialised.current = true;

      const columnVisibility = (options?.columnDefinitions || []).reduce((o, columnDef) => {
        if (utils.isDefined(columnDef.visible)) {
          o = o ?? {};
          o[columnDef.id] = columnDef.visible;
        }
        return o;
      }, options?.columnState?.initial?.columnVisibility);

      const columnPinning = (options?.columnDefinitions || []).reduce((o, columnDef) => {
        if (columnDef.pinned) {
          o = {left: [], right: [], ...o};
          o[columnDef.pinned].push(columnDef.id);
        }
        return o;
      }, options?.columnState?.initial?.columnPinning);

      const panelVisibility = (options?.panelDefinitions || []).reduce((o, panelDef) => {
        if (utils.isDefined(panelDef.visible)) {
          o = o ?? {};
          o[panelDef.name] = panelDef.visible;
        }
        return o;
      }, options?.kanbanState?.initial?.panelVisibility);

      const graphVisibility = (options?.graphDefinitions || []).reduce((o, graphDef) => {
        if (utils.isDefined(graphDef.visible)) {
          o = o ?? {};
          o[graphDef.name] = graphDef.visible;
        }
        return o;
      }, options?.graphState?.initial?.graphVisibility);

      return {
        ...options,
        listSelection: {
          ...options?.listSelection,
          options: {
            ...options?.listSelection?.options,
            dataKey: props.dataKey
          }
        },
        columnState: {
          ...options?.columnState,
          initial: {
            ...options?.columnState?.initial,
            columnVisibility: columnVisibility,
            columnPinning: columnPinning
          },
          options: smDown ? {
            ...options?.columnState?.options,
            name: (options?.columnState?.options?.name ?? '') + '_smDown'
          } : options?.columnState?.options
        },
        kanbanState: {
          ...options?.kanbanState,
          initial: {
            ...options?.kanbanState?.initial,
            panelVisibility: panelVisibility
          }
        },
        graphState: {
          ...options?.graphState,
          initial: {
            ...options?.graphState?.initial,
            graphVisibility: graphVisibility
          }
        }
      };
    } else {
      return {
        listState: {
          initial: {
            active: false
          },
          options: {
            searchParams: options?.listState?.options?.searchParams ?? false,
            searchParamsRoutes: options?.listState?.options?.searchParamsRoutes ?? null
          }
        },
        testListState: {
          initial: {
            active: false
          },
          options: {
            searchParams: options?.testListState?.options?.searchParams ?? false,
            searchParamsRoutes: options?.testListState?.options?.searchParamsRoutes ?? null
          }
        },
        kanbanListState: {
          initial: {
            active: false
          },
          options: {
            searchParams: options?.kanbanListState?.options?.searchParams ?? false,
            searchParamsRoutes: options?.kanbanListState?.options?.searchParamsRoutes ?? null
          }
        }
      };
    }
  }, [props.dataKey, props?.options, props?.skipStorage, props?.view, props?.isDefinitionsLoading, smDown]);

  const listState = useListState(optionsMemo?.listState?.initial, optionsMemo?.listState?.options);
  const baseListState = useListState(optionsMemo?.baseListState?.initial, optionsMemo?.baseListState?.options);
  const testListState = useListState(optionsMemo?.testListState?.initial, optionsMemo?.testListState?.options);
  const kanbanListState = useListState(optionsMemo?.kanbanListState?.initial, optionsMemo?.kanbanListState?.options);
  const columnState = useColumnState(optionsMemo?.columnState?.initial, optionsMemo?.columnState?.options);
  const kanbanState = useKanbanState(optionsMemo?.kanbanState?.initial, optionsMemo?.kanbanState?.options);
  const graphState = useGraphState(optionsMemo?.graphState?.initial, optionsMemo?.graphState?.options);
  const assistantState = useAssistantState(optionsMemo?.assistantState?.initial, optionsMemo?.assistantState?.options);

  const listSelection = useListSelection(optionsMemo?.listSelection?.initial, optionsMemo?.listSelection?.options, internalState.list);

  const defaultsMemo = useMemo(() => {
    const defaults = utils.mergeObjects(props.defaults, props?.view?.defaults, true);

    const sort = (optionsMemo?.columnDefinitions || []).reduce((a, columnDef) => {
      if (columnDef.sorted) {
        const sorted = utils.isFunction(columnDef.sorted) ? columnDef.sorted(props?.view) : columnDef.sorted;
        a = utils.addSort(a, {id: columnDef.id, desc: sorted === 'desc'});
      }
      return a;
    }, defaults?.list?.sort);

    const filter = (optionsMemo?.filterGroupDefinitions || []).reduce((a, filterGroupDef) => {
      Object.keys(filterGroupDef.filters || {}).forEach((k) => {
        const filter = filterGroupDef.filters[k];
        if (filter.filtered) {
          const switched = utils.isFunction(filter.switched) ? filter.switched(props?.view) : filter.switched;
          const filtered = utils.isFunction(filter.filtered) ? filter.filtered(props?.view) : filter.filtered;
          const filterId = utils.isFunction(filter.id) ? filter.id(props?.view) : filter.id;

          a = utils.applyFilters(a, {id: filterId, value: filtered}, filterId);
          if (switched) {
            a = utils.addFilter(a, {id: 'switch', value: filterId});
          }
        }
      });
      return a;
    }, defaults?.list?.filter);

    return {
      ...defaults,
      list: {
        ...defaults?.list,
        sort: sort,
        filter: filter
      }
    };
  }, [props.defaults, props?.view, optionsMemo?.columnDefinitions, optionsMemo?.filterGroupDefinitions]);

  const authorize = useAuthorize();
  const tour = useTour();

  const columnDefinitions = useMemo(() => {
    const columnDefs = optionsMemo?.columnDefinitions;
    if (columnDefs?.length > 0) {
      return columnDefs
        .filter((columnDef) => !columnDef.hidden && authorize(columnDef.auth?.read ?? {}));
    } else {
      return null;
    }
  }, [authorize, optionsMemo?.columnDefinitions]);

  const filterGroupDefinitions = useMemo(() => {
    const filterGroupDefs = optionsMemo?.filterGroupDefinitions;
    if (filterGroupDefs?.length > 0) {
      return filterGroupDefs
        .filter((filterGroupDef) => !filterGroupDef.hidden && authorize(filterGroupDef.auth?.read ?? {}))
        .map((filterGroupDef) => {
          filterGroupDef.filters = (filterGroupDef.filters ?? [])
            .filter((f) => !f.hidden && authorize(f.auth?.read ?? {}));
          return filterGroupDef;
        });
    } else {
      return null;
    }
  }, [authorize, optionsMemo?.filterGroupDefinitions]);

  const panelDefinitions = useMemo(() => {
    const panelDefs = optionsMemo?.panelDefinitions;
    if (panelDefs?.length > 0) {
      return panelDefs
        .filter((panelDef) => !panelDef.hidden && authorize(panelDef.auth?.read ?? {}))
        .map((panelDef) => {
          panelDef.subPanels = panelDef.subPanels
            .filter((panelDef) => !panelDef.hidden && authorize(panelDef.auth?.read ?? {}));
          return panelDef;
        });
    } else {
      return null;
    }
  }, [authorize, optionsMemo?.panelDefinitions]);

  const graphDefinitions = useMemo(() => {
    const graphDefs = optionsMemo?.graphDefinitions;
    if (graphDefs?.length > 0) {
      return graphDefs
        .filter((graphDef) => !graphDef.hidden && authorize(graphDef.auth?.read ?? {}));
    } else {
      return null;
    }
  }, [authorize, optionsMemo?.graphDefinitions]);

  const compareEvent = useEffectEvent(props.onCompare)
  const onCompare = useCallback((itmA, itmB) => {
    return compareEvent ? compareEvent?.(itmA, itmB) : (
      props.dataKey ? itmA[props.dataKey] === itmB[props.dataKey] : itmA === itmB
    )
  }, [compareEvent, props.dataKey]);

  const dataRef = useRef(null);
  const dataMemo = useMemo(() => {
    if (utils.isDefined(props.data?.data) || props.data?.status?.hasError) {
      // keep the last available data
      dataRef.current = props.data;
    }
    return dataRef.current;
  }, [props.data]);

  const viewId = utils.isDefined(props.view?.id) ? (props.view?.id + (smDown ? '_smDown' : '')) : 'default';
  const initialised = listState?.active &&
    (!listState?.view || +listState?.view.view === +props.view?.id || +props.view?.id === -1) &&
    internalState.activeView === viewId;
  const context = useMemo(() => {
    const appliedListState = (type, skipAssistant) => {
      const defaultFilter = defaultsMemo?.list?.filter ?? null;
      const defaultSort = defaultsMemo?.list?.sort ?? null;

      const originalState = type ? (type === 'test' ? testListState : kanbanListState) : listState;

      let appliedQuery = originalState.query;
      let appliedFilter = utils.addFilter(defaultFilter, originalState.filter);
      let appliedSort = !utils.isEmpty(originalState.sort) ? originalState.sort : defaultSort;

      let listCustom = originalState.custom;
      let viewCustom = props.view?.userView?.settings?.list?.custom;
      if (viewCustom?.length > 0) {
        listCustom = utils.uniqueArray(viewCustom.concat(listCustom ?? []));
      }

      let filteredByAssistant = false;
      if (!skipAssistant) {
        const processedMessage = assistant.processedMessage(assistantState.assistantType, assistantState.assistantConversation);

        if (utils.isDefined(processedMessage)) {
          let entityIds = processedMessage?.results?.entities?.map((e) => +e.entityId);
          let analyseFilter = `analyse:${btoa(JSON.stringify([-1]))}`;
          if (entityIds?.length > 0) {
            analyseFilter = `analyse:${btoa(JSON.stringify(entityIds))}`;
          }

          if (appliedQuery) {
            if (appliedQuery?.assistant) {
              appliedQuery = {
                ...appliedQuery,
                parts: [{
                  queryType: 'AND',
                  filter: {
                    custom: analyseFilter
                  }
                }]
              };
            }
          } else {
            listCustom = utils.uniqueArray((listCustom ?? []).concat([analyseFilter]));
          }
        }
      }

      if (listCustom?.length > 0) {
        listCustom = listCustom.map((c) => ({id: 'custom', value: c}))
          .concat(listCustom?.length > 1 ? [{id: 'switch', value: 'custom'}] : []);

        appliedFilter = utils.addFilter(appliedFilter, listCustom);

        if (!filteredByAssistant && appliedQuery) {
          appliedQuery = {
            ...appliedQuery,
            filters: utils.filter2Object(listCustom)
          }
        }
      }

      return {
        ...originalState,
        search: originalState.search,
        query: appliedQuery,
        filter: appliedFilter,
        sort: appliedSort,
        pagination: originalState.pagination
      };
    }

    return {
      state: {
        ...internalState,
        ...props.state
      },
      listState,
      testListState,
      kanbanListState,
      columnState,
      listSelection,
      kanbanState,
      graphState,
      assistantState,
      initialised,
      columnDefinitions,
      panelDefinitions,
      graphDefinitions,
      filterGroupDefinitions,
      list: internalState.list,
      testList: internalState.testList,
      kanbanLists: internalState.kanbanLists,
      data: dataMemo,
      context: props.context,
      view: props.view,
      views: props.views,
      dataKey: props.dataKey,
      onCompare: onCompare,
      fieldData: props.fieldData,
      updaters: props.updaters,
      defaults: defaultsMemo,
      clearState: () => {
        listState?.clear?.();
        columnState?.clear?.();
        kanbanState?.clear?.();
        graphState?.clear?.();
        assistantState?.clear?.();
        listSelection?.clearSelection?.();
        setInternalState((current) => {
          return utils.updater({
            ...current,
            // see also viewInit
            showIndex: false,
            showFilters: false,
            showColumns: false,
            showSave: false,
            showAssistant: false,
            showViews: false,
            showKanban: current.showKanban,
            showProfile: false,
            showQuery: true,
            viewData: null,
            settings: {}
          })(current);
        });
      },
      isLoading: () => {
        return Boolean(props.isLoading || (internalState.list?.status?.isLoading || !internalState.list?.status?.isSuccess));
      },
      appliedListState: appliedListState,
      first: () => {
        return internalState.list?.data?.[0];
      },
      find: () => {
        if (dataMemo?.data && internalState.list?.data) {
          return internalState.list.data.find((d) => onCompare(d, dataMemo.data));
        } else {
          return null;
        }
      },
      selected: () => {
        return dataMemo?.status?.isLoading ? dataMemo?.data : (
          dataMemo?.data ?? internalState.list?.data?.[0]
        );
      },
      next: (count = 1) => {
        if (count > 0 && internalState.list?.data?.length > 0) {
          let idx = 0;
          if (dataMemo?.data && internalState.list?.data) {
            const foundIdx = internalState.list.data.findIndex((d) => onCompare(d, dataMemo.data));
            idx = foundIdx !== -1 ? foundIdx : idx;
          }

          const length = internalState.list.data.length;
          const loop = internalState.list.meta.resultsCount === length;
          const newIndex = (count === 1 || loop) ? (idx + Math.min(count, length - 1)) :
            Math.min(idx + count, internalState.list.data.length - 1);
          if (loop || newIndex < internalState.list.data.length) {
            const itmIndex = (newIndex < 0 ? (length + newIndex) : newIndex) % length;
            if (newIndex !== idx) {
              if (count === 1) {
                return internalState.list.data[itmIndex];
              } else {
                if (loop && itmIndex <= idx) {
                  return internalState.list.data.slice(idx + 1).concat(internalState.list.data.slice(0, itmIndex + 1));
                } else {
                  return internalState.list.data.slice(idx + 1, itmIndex + 1);
                }
              }
            }
          }
        }
        return null;
      },
      prev: (count = 1) => {
        if (count > 0 && internalState.list?.data?.length > 0) {
          let idx = 0;
          if (dataMemo?.data && internalState.list?.data) {
            const foundIdx = internalState.list.data.findIndex((d) => onCompare(d, dataMemo.data));
            idx = foundIdx !== -1 ? foundIdx : idx;
          }

          const length = internalState.list.data.length;
          const loop = internalState.list.meta.resultsCount === length;
          const newIndex = (count === 1 || loop) ? (idx - Math.min(count, length - 1)) :
            Math.max(0, idx - count);
          if (loop || newIndex >= 0) {
            const itmIndex = (newIndex < 0 ? (length + newIndex) : newIndex) % length;
            if (newIndex !== idx) {
              if (count === 1) {
                return internalState.list.data[itmIndex];
              } else {
                if (loop && itmIndex >= idx) {
                  return internalState.list.data.slice(0, idx).concat(internalState.list.data.slice(itmIndex)).reverse();
                } else {
                  return internalState.list.data.slice(itmIndex, idx).reverse();
                }
              }
            }
          }
        }
        return null;
      },
      reset: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            reset: current.reset > 0 ? (+current.reset + 1) : 1
          })(current);
        });
      },
      preload: (ids) => {
        setInternalState(utils.updater({preload: ids}, true));
      },
      toggleIndex: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showIndex: !current.showIndex
          })(current);
        });
      },
      openIndex: () => {
        setInternalState(utils.updater({showIndex: true}, true));
      },
      closeIndex: () => {
        setInternalState(utils.updater({showIndex: false}, true));
      },
      viewSettings: (settings) => {
        if (settings) {
          listState.setSearch(settings?.list?.search ?? null);
          listState.setSort(settings?.list?.sort ?? null);
          listState.setFilter(settings?.list?.filter ?? null);
          listState.setPagination(settings?.list?.pagination ?? null);
          listState.setQuery(settings?.list?.query ?? null);

          if (!smDown) {
            columnState.setColumnOrder(settings?.column?.columnOrder ?? null);
            columnState.setColumnVisibility(settings?.column?.columnVisibility ?? null);
            columnState.setColumnPinning(settings?.column?.columnPinning ?? null);
            columnState.setColumnSizing(settings?.column?.columnSizing ?? null);
          }

          kanbanState.setPanelOrder(settings?.kanban?.panelOrder ?? null);
          kanbanState.setPanelVisibility(settings?.kanban?.panelVisibility ?? null);
          kanbanState.setPanelSettings(settings?.kanban?.panelSettings ?? null);

          graphState.setGraphOrder(settings?.graph?.graphOrder ?? null);
          graphState.setGraphVisibility(settings?.graph?.graphVisibility ?? null);
          graphState.setGraphSettings(settings?.graph?.graphSettings ?? null);

          assistantState.setAssistantType(settings?.assistant?.assistantType ?? null);
          assistantState.setAssistantConversation(settings?.assistant?.assistantConversation ?? null);
          assistantState.setAssistantActions(settings?.assistant?.assistantActions ?? null);
          assistantState.setAssistantSettings(settings?.assistant?.assistantSettings ?? null);
        } else {
          let listSort = listState.cleanSort(listState.sort);
          let listFilter = listState.cleanFilter(listState.filter);

          let listCustom = listState.custom;
          let viewCustom = props.view?.userView?.settings?.list?.custom;
          if (!utils.isEmpty(viewCustom)) {
            listCustom = utils.uniqueArray(viewCustom.concat(listCustom ?? []));
          }

          const lastStateApplied = appliedListState();

          return {
            list: {
              search: listState.search,
              query: listState.query,
              custom: listCustom,
              sort: listSort,
              filter: listFilter,
              pagination: listState.pagination,
              applied: { // used for notifications
                search: lastStateApplied.search,
                query: lastStateApplied.query,
                custom: lastStateApplied.custom,
                sort: lastStateApplied.sort,
                filter: lastStateApplied.filter,
                pagination: lastStateApplied.pagination
              }
            },
            column: {
              ...(!smDown ? {
                columnOrder: columnState.columnOrder,
                columnVisibility: columnState.columnVisibility,
                columnPinning: columnState.columnPinning,
                columnSizing: columnState.columnSizing
              } : props.view?.userView?.settings?.column)
            },
            kanban: {
              panelOrder: kanbanState.panelOrder,
              panelVisibility: kanbanState.panelVisibility,
              panelSettings: kanbanState.panelSettings
            },
            graph: {
              graphOrder: graphState.graphOrder,
              graphVisibility: graphState.graphVisibility,
              graphSettings: graphState.graphSettings
            },
            ...(
              (constants.assistant.types.search === assistantState.assistantType) ? {
                assistant: {
                  assistantType: assistantState.assistantType,
                  assistantConversation: assistantState.assistantConversation,
                  assistantActions: assistantState.assistantActions,
                  assistantSettings: assistantState.assistantSettings
                }
              } : {}
            )
          };
        }
      },
      toggleSave: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showSave: !current.showSave
          })(current);
        });
      },
      openSave: () => {
        setInternalState(utils.updater({showSave: true}, true));
      },
      closeSave: () => {
        setInternalState(utils.updater({showSave: false}, true));
      },
      toggleAssistant: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showAssistant: !current.showAssistant
          })(current);
        });
      },
      openAssistant: () => {
        setInternalState(utils.updater({showAssistant: true}, true));
      },
      closeAssistant: () => {
        setInternalState(utils.updater({showAssistant: false}, true));
      },
      toggleViews: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showViews: !current.showViews
          })(current);
        });
      },
      openViews: () => {
        setInternalState(utils.updater({showViews: true}, true));
      },
      closeViews: () => {
        setInternalState(utils.updater({showViews: false}, true));
      },
      toggleFilters: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showFilters: !current.showFilters
          })(current);
        });
      },
      openFilters: () => {
        setInternalState(utils.updater({showFilters: true}, true));
      },
      closeFilters: () => {
        setInternalState(utils.updater({showFilters: false}, true));
      },
      toggleColumns: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showColumns: !current.showColumns
          })(current);
        });
      },
      openColumns: () => {
        setInternalState(utils.updater({showColumns: true}, true));
      },
      closeColumns: () => {
        setInternalState(utils.updater({showColumns: false}, true));
      },
      toggleKanban: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showKanban: !current.showKanban
          })(current);
        });
      },
      openKanban: () => {
        setInternalState(utils.updater({showKanban: true}, true));
      },
      closeKanban: () => {
        setInternalState(utils.updater({showKanban: false}, true));
      },
      openProfile: (id, settings) => {
        setInternalState(utils.updater({showProfile: {
          id,
          settings
        }}, true));
      },
      closeProfile: () => {
        setInternalState(utils.updater({showProfile: false}, true));
      },
      toggleQuery: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showQuery: !current.showQuery
          })(current);
        });
      },
      openQuery: () => {
        setInternalState(utils.updater({showQuery: true}, true));
      },
      closeQuery: () => {
        setInternalState(utils.updater({showQuery: false}, true));
      },
      setViewData: (data) => {
        if (!utils.isFunction(data)) {
          setInternalState((current) => {
            return utils.updater({
              ...current,
              viewData: {
                ...current.viewData,
                ...data
              }
            })(current);
          });
        } else {
          setInternalState((current) => {
            return utils.updater({
              ...current,
              viewData: data(current.viewData)
            })(current);
          });
        }
      },
      setSettings: (settings) => {
        if (!utils.isFunction(settings)) {
          setInternalState((current) => {
            return utils.updater({
              ...current,
              settings: {
                ...current.settings,
                ...settings
              }
            })(current);
          });
        } else {
          setInternalState((current) => {
            return utils.updater({
              ...current,
              settings: settings(current.settings)
            })(current);
          });
        }
      },
      setList: (list) => {
        setInternalState((current) => ({
          ...current,
          list
        }));
      },
      setTestList: (list) => {
        setInternalState((current) => ({
          ...current,
          testList: list
        }));
      },
      setKanbanList: (id, list) => {
        setInternalState((current) => ({
          ...current,
          kanbanLists: {
            ...current.kanbanLists,
            [id]: list
          }
        }));
      }
    }
  }, [internalState, listState, testListState, kanbanListState, initialised,
    columnState, listSelection, kanbanState, graphState, assistantState, props.view, props.views,
    props.isLoading, props.fieldData, props.updaters, defaultsMemo, props.dataKey,
    columnDefinitions, panelDefinitions, graphDefinitions, filterGroupDefinitions,
    props.state, props.context, dataMemo, onCompare, smDown]);

  const reportEvent = useEffectEvent(tour?.report);
  useLayoutEffect(() => {
    if (props.view?.type) {
      reportEvent?.(`table:${props.view.type}`, context);

      return () => {
        reportEvent?.(`table:${props.view.type}`, null);
      };
    }
  }, [reportEvent, props.view?.type, context]);

  const touchViewEvent = useEffectEvent(context.updaters?.touchView);
  const viewSettingsEvent = useEffectEvent(context.viewSettings);
  useLayoutEffect(() => {
    if (listState?.active) {
      const refresh = props.view?.userView &&
        (!listState.view?.appliedAt || !props.view.userView.updatedAt ||
          (new Date(listState.view.appliedAt)).getTime() < (new Date(props.view.userView.updatedAt)).getTime());

      if (refresh || viewInitialised.current !== viewId) {
        const init = viewInitialised.current !== viewId;

        viewInitialised.current = viewId;
        setInternalState((current) => {
          return utils.updater({
            ...current,
            activeView: viewId,
            ...(init ? {
              // see also clearState
              showIndex: false,
              showFilters: false,
              showColumns: false,
              showSave: false,
              showAssistant: false,
              showViews: false,
              showKanban: current.showKanban,
              showProfile: false,
              showQuery: true,
              viewData: null,
              settings: {}
            } : null)
          })(current);
        });

        if (props.view?.userView) {
          if (+props.view?.userView?.viewId > 0) {
            viewSettingsEvent?.(props.view.userView.settings);
            listState.setView({
              view: props.view.userView.viewId,
              appliedAt: (new Date(props.view.userView.updatedAt)).toISOString()
            });
            baseListState.setView({
              view: props.view.userView.viewId,
              appliedAt: (new Date(props.view.userView.updatedAt)).toISOString()
            });

            touchViewEvent?.({viewId: props.view.userView.viewId});
          } else {
            listState.setView(null);
            baseListState.setView(null);
          }
        } else {
          baseListState.setView(null);
        }
      }
    }
  }, [viewId, props.view?.userView, listState, baseListState, viewSettingsEvent, touchViewEvent, smDown]);

  useLayoutEffect(() => {
    // detect reordering / filtering etc.
    if (listState?.active) {
      const hash = utils.sha1(utils.filterObject(listState, 'scroll'));
      if (internalState.list?.status?.isSuccess && listInitialised.current !== hash) {
        listInitialised.current = hash;
        setInternalState((current) => {
          return utils.updater({
            ...current,
            preload: [],
            reset: current.reset > 0 ? (+current.reset + 1) : 1
          })(current);
        });
      }
    }
  }, [listState, internalState.list?.status?.isSuccess]);

  useLayoutEffect(() => {
    if (!props?.isDefinitionsLoading && utils.isDefined(optionsMemo?.columnDefinitions)) {
      // detect missing columns, auto reset pinning
      const leftMissing = columnState.columnPinning?.left?.find((c) => !optionsMemo?.columnDefinitions?.find((cd) => cd.id === c));
      const rightMissing = columnState.columnPinning?.right?.find((c) => !optionsMemo?.columnDefinitions?.find((cd) => cd.id === c));

      if (leftMissing || rightMissing) {
        columnState.setColumnPinning((current) => ({
          left: (leftMissing ? optionsMemo?.columnState?.initial?.columnPinning?.left : current?.left) ?? [],
          right: (rightMissing ? optionsMemo?.columnState?.initial?.columnPinning?.right : current?.right) ?? []
        }));
      }
    }
  }, [columnState, props?.isDefinitionsLoading, optionsMemo?.columnDefinitions, optionsMemo?.columnState?.initial]);

  const LoaderComponent = props.LoaderComponent;
  const TestLoaderComponent = props.TestLoaderComponent;
  const KanbanLoaderComponent = props.KanbanLoaderComponent;
  const PreLoaderComponent = props.PreLoaderComponent;

  const active = (props.active ?? true) && initialised;
  return <TableContext.Provider value={context}>
    {LoaderComponent ? <LoaderComponent active={active} /> : null}
    {TestLoaderComponent ? <TestLoaderComponent active={active} /> : null}
    {KanbanLoaderComponent ? <KanbanLoaderComponent active={active && context.state.showKanban} /> : null}
    {PreLoaderComponent ? internalState.preload.map((id) => {
      return <PreLoaderComponent key={id} active={active} id={id}/>
    }).filter((_) => (_)) : null}

    {props.children}
  </TableContext.Provider>
};

TableProvider.propTypes = {
  view: PropTypes.object,
  data: PropTypes.object,
  context: PropTypes.object,
  options: PropTypes.object,
  updaters: PropTypes.object,
  defaults: PropTypes.object,
  views: PropTypes.object,
  state: PropTypes.object,
  onCompare: PropTypes.func,
  isLoading: PropTypes.bool,
  isDefinitionsLoading: PropTypes.bool,
  fieldData: PropTypes.object,
  LoaderComponent: PropTypes.any,
  TestLoaderComponent: PropTypes.any,
  KanbanLoaderComponent: PropTypes.any,
  PreLoaderComponent: PropTypes.any
}

export default TableProvider;
